import { Parser } from "m3u8-parser";
import { useEffect, useState } from "react";

import { fetchThumbnailManifest } from "./fetchThumbnailManifest";
import { generateSignedUrl } from "./generatePreSignedUrl";

import type { Cookie } from "@spwn/types/functions";
import type { Segment } from "m3u8-parser";
import type Player from "video.js/dist/types/player";

/** サムネイルURL取得のポーリング間隔（ミリ秒） */
const POLLING_INTERVAL_MS = 5000;

export const useGetVideoThumbnailPresignedUrlForVOD = ({
  cookie: { url: cookieUrl, cookie },
  player,
}: {
  cookie: Pick<Cookie, "url" | "cookie">;
  player: Player;
}): string | undefined => {
  const [thumbnailSegmentList, setThumbnailSegmentList] = useState<
    Segment[] | undefined
  >(undefined);

  const [currentThumbnailSegmentUri, setCurrentThumbnailSegmentUri] = useState<
    string | undefined
  >(undefined);

  // サムネイルセグメントリストを取得
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const thumbnailManifestFileData = await fetchThumbnailManifest({
          cookie: { url: cookieUrl, cookie },
        });

        if (thumbnailManifestFileData === undefined) {
          return;
        }

        const parser = new Parser();
        parser.push(thumbnailManifestFileData);
        parser.end();

        const parsedManifest = parser.manifest.segments;
        setThumbnailSegmentList(parsedManifest);
      } catch (error) {
        console.error("error happened in useGetPresignedUrl", error);
      }
    };

    void fetchData();
  }, [cookieUrl, cookie]);

  // 現在の再生時間に対応するセグメントURIを計算
  useEffect(() => {
    if (thumbnailSegmentList === undefined) {
      return;
    }

    // 初回実行
    setCurrentThumbnailSegmentUri(
      getCurrentSegmentPresignedUrl({
        player,
        thumbnailSegmentList,
        cookieUrl,
        cookie,
      })
    );

    // ポーリング実行
    const intervalId = setInterval(() => {
      const currentSegmentUri = getCurrentSegmentPresignedUrl({
        player,
        thumbnailSegmentList,
        cookieUrl,
        cookie,
      });
      setCurrentThumbnailSegmentUri(currentSegmentUri);
    }, POLLING_INTERVAL_MS);

    // クリーンアップ
    return () => clearInterval(intervalId);
  }, [thumbnailSegmentList, player, cookieUrl, cookie]);

  console.log("currentThumbnailSegmentUri", currentThumbnailSegmentUri);

  return currentThumbnailSegmentUri;
};

const getCurrentSegmentUri = (
  player: Player,
  thumbnailSegmentList: Segment[]
): string | undefined => {
  let accumulatedTime = 0;

  const currentPlayerTime = player.currentTime();
  for (const segment of thumbnailSegmentList) {
    if (
      currentPlayerTime !== undefined &&
      currentPlayerTime > accumulatedTime &&
      currentPlayerTime <= accumulatedTime + segment.duration
    ) {
      // query parameterを除去してURIのみを返す
      return segment.uri.split("?")[0];
    }

    accumulatedTime += segment.duration;
  }
  return undefined;
};

const getCurrentSegmentPresignedUrl = ({
  player,
  thumbnailSegmentList,
  cookieUrl,
  cookie,
}: {
  player: Player;
  thumbnailSegmentList: Segment[];
  cookieUrl: string;
  cookie: Cookie["cookie"];
}): string | undefined => {
  const segmentUri = getCurrentSegmentUri(player, thumbnailSegmentList);

  if (segmentUri === undefined) {
    return undefined;
  }

  return generateSignedUrl({
    manifestFileUrl: cookieUrl,
    cookie,
    fileName: segmentUri,
  });
};
